import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { addDoc, collection, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import "../styles/PuppyApplication.css";
import { db, storage } from "../firebase";
import {
  STORAGE_KEY,
  normalizeDraft,
  validateAll,
  buildReviewSections,
  submitApplication,
} from "./PuppyApplicationPage";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_APPLICATION;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const BREEDER_FIRST_NAME = "Cary";

const contactMethodLabels = {
  call: "Call",
  text: "Text",
  email: "Email",
};

const formatPhone = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value || "";
};

const buildStoragePath = (form, submittedAt) => {
  const name = (form.applicant.fullName || "applicant").trim().toLowerCase();
  const safeName = name
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const timestamp = submittedAt.toISOString().replace(/[:.]/g, "-");
  return `puppy-applications/${timestamp}-${safeName || "applicant"}.pdf`;
};

const uploadPdfAndGetUrl = async (pdfBlob, form) => {
  const submittedAt = new Date();
  const storagePath = buildStoragePath(form, submittedAt);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, pdfBlob, { contentType: "application/pdf" });
  const pdfUrl = await getDownloadURL(storageRef);
  return { pdfUrl, storagePath, submittedAt };
};

const buildHousingSummary = (form) => {
  const parts = [];
  const residence =
    form.housing.residenceType === "other"
      ? form.housing.residenceTypeOther
      : form.housing.residenceType;
  if (residence) {
    parts.push(residence.charAt(0).toUpperCase() + residence.slice(1));
  }
  if (form.housing.ownOrRent) {
    parts.push(form.housing.ownOrRent === "own" ? "Own" : "Rent");
  }
  if (form.housing.yardFlag !== null) {
    parts.push(form.housing.yardFlag ? "Yard" : "No yard");
  }
  if (form.housing.yardFlag && form.housing.fencedFlag !== null) {
    parts.push(form.housing.fencedFlag ? "Fenced yard" : "No fence");
  }
  return parts.join(", ");
};

const buildCurrentPetsSummary = (form) => {
  if (form.experience.currentlyOwnPets === true) {
    return form.experience.currentPetsDetails || "Yes";
  }
  if (form.experience.currentlyOwnPets === false) {
    return "No";
  }
  return "";
};

const buildTemplateParams = (form, pdfUrl, submittedAt, recipientEmail) => {
  const applicantName = form.applicant.fullName || "";
  const applicantEmail = form.applicant.email || "";
  const applicantPhone = formatPhone(form.applicant.phone);
  const preferredContact =
    contactMethodLabels[form.applicant.preferredContactMethod] || "";
  const householdSummary = form.household.members || "";
  const housingSummary = buildHousingSummary(form);
  const currentPetsSummary = buildCurrentPetsSummary(form);
  const timeframe = form.preferences.timeframe || "";
  const submittedAtText = submittedAt.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const submittedDateText = submittedAt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return {
    email: recipientEmail,
    reply_email: applicantEmail,
    applicant_name: applicantName,
    submitted_date: submittedDateText,
    breeder_first_name: BREEDER_FIRST_NAME,
    applicant_email: applicantEmail,
    applicant_phone: applicantPhone,
    preferred_contact_method: preferredContact || "Not provided",
    submitted_at: submittedAtText,
    household_summary: householdSummary || "Not provided",
    housing_summary: housingSummary || "Not provided",
    current_pets_summary: currentPetsSummary || "Not provided",
    timeframe: timeframe || "Not provided",
    applicant_message: "No additional message provided.",
    phone_row_style: applicantPhone ? "" : "display:none;",
    email_row_style: applicantEmail ? "" : "display:none;",
    preferred_contact_row_style: preferredContact ? "" : "display:none;",
    household_row_style: householdSummary ? "" : "display:none;",
    housing_row_style: housingSummary ? "" : "display:none;",
    pets_row_style: currentPetsSummary ? "" : "display:none;",
    timeframe_row_style: timeframe ? "" : "display:none;",
    application_pdf_url: pdfUrl,
  };
};

export default function PuppyApplicationReviewPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        setLoadError("No saved application found. Please complete the form first.");
        setHasDraft(false);
        return;
      }
      const parsed = JSON.parse(saved);
      const normalized = normalizeDraft(parsed);
      if (!normalized) {
        setLoadError("Unable to load the saved application.");
        setHasDraft(false);
        return;
      }
      setForm(normalized);
      setHasDraft(true);
    } catch (error) {
      setLoadError("Unable to load the saved application.");
      setHasDraft(false);
    } finally {
      setIsReady(true);
    }
  }, []);

  const reviewSections = useMemo(
    () => (form ? buildReviewSections(form) : []),
    [form]
  );

  const validationErrors = useMemo(
    () => (form ? validateAll(form) : {}),
    [form]
  );

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleBack = () => {
    navigate("/puppy-application");
  };

  const handleSubmit = async () => {
    if (!form) return;
    setIsSubmitting(true);
    try {
      if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
        throw new Error("Email service is not configured.");
      }
      const settingsRef = doc(db, "settings", "basicInfo");
      const settingsSnap = await getDoc(settingsRef);
      const notificationEmails = settingsSnap.exists()
        ? settingsSnap.data()?.notificationEmails || []
        : [];
      const recipientEmail = notificationEmails
        .filter(Boolean)
        .map((email) => String(email).trim())
        .filter((email) => email.length > 0)
        .join(", ");
      if (!recipientEmail) {
        throw new Error("Notification email list is not configured.");
      }
      console.log("Puppy application payload:", form);
      const { pdfBlob } = await submitApplication(form);
      const { pdfUrl, storagePath, submittedAt } = await uploadPdfAndGetUrl(
        pdfBlob,
        form
      );
      await addDoc(collection(db, "puppyApplications"), {
        applicantName: form.applicant.fullName || "",
        applicantEmail: form.applicant.email || "",
        applicantPhone: formatPhone(form.applicant.phone),
        preferredContactMethod: form.applicant.preferredContactMethod || "",
        timeframe: form.preferences.timeframe || "",
        pdfUrl,
        pdfPath: storagePath,
        status: "submitted",
        submittedAt: serverTimestamp(),
      });
      const templateParams = buildTemplateParams(
        form,
        pdfUrl,
        submittedAt,
        recipientEmail
      );
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // ignore storage errors
      }
      navigate("/puppy-application/success");
    } catch (error) {
      const rawMessage = error?.message || "";
      const message = rawMessage.toLowerCase().includes("fetch")
        ? "Submission service is not reachable. Please try again or contact us."
        : rawMessage || "Submission failed. Please try again or contact us.";
      navigate("/puppy-application/failed", { state: { message } });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="puppy-app-page">
      <div className="puppy-app-header fade-up">
        <div>
          <p className="puppy-app-kicker">Puppy Purchase Application</p>
          <h1 className="puppy-app-title">Review Application</h1>
        </div>
        <p className="puppy-app-intro">
          Review the details below before submitting. You can go back to edit the
          application at any time.
        </p>
      </div>

      <div className="puppy-app-form fade-up">
        {isSubmitting && (
          <div className="submit-overlay" role="status" aria-live="polite">
            <div className="submit-overlay-card">
              <span className="spinner" aria-hidden="true" />
              <div>
                <div className="submit-overlay-title">Submitting application</div>
                <div className="submit-overlay-text">
                  Please keep this page open while we submit your application.
                </div>
              </div>
            </div>
          </div>
        )}
        {loadError && (
          <div className="warning-banner" role="status">
            {loadError}
          </div>
        )}
        {isReady && hasDraft && hasErrors && (
          <div className="warning-banner" role="status">
            Some required fields are missing. Please return to edit before
            submitting.
          </div>
        )}

        {!isReady && (
          <div className="warning-banner" role="status">
            Loading saved application...
          </div>
        )}

        {isReady && !hasDraft && (
          <div className="review-actions">
            <button type="button" className="primary-btn" onClick={handleBack}>
              Go to Application
            </button>
          </div>
        )}

        {isReady && hasDraft && (
          <>
            <div className="review-panel">
              <h3>Application Summary</h3>
              {reviewSections.map((section) => (
                <div key={section.title} className="review-section">
                  <h4>{section.title}</h4>
                  <div className="review-grid">
                    {section.items.map((item) => (
                      <div key={item.label} className="review-item">
                        <span className="review-label">{item.label}</span>
                        <span className="review-value">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <section className="puppy-section">
              <div className="section-header">
                <h2>Confirmation</h2>
                <p>
                  By submitting, you confirm the information provided is accurate
                  and complete.
                </p>
              </div>
              <div className="review-actions">
                <button type="button" className="secondary-btn" onClick={handleBack}>
                  Back to Edit
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleSubmit}
                  disabled={!hasDraft || hasErrors || isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>
              </div>
              <p className="review-note">
                Submissions are sent when you click the submit button above.
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
